import React, { useState, useRef, useContext } from 'react';
import { Card, Badge, Button, Spinner, Collapse, Form } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaStar, FaRegStar, FaCamera, FaTimes } from 'react-icons/fa';
import { formatDate, formatRelativeTime } from '../utils/dateFormatter';
import { formatCount, formatProgress, formatClockDuration } from '../utils/numberFormatter';
import useCleanTargetTimer from '../hooks/useCleanTargetTimer';
import { taskRefusalKey } from '../utils/taskErrorCodes';
import useLanguage from '../hooks/useLanguage';
import taskApi from '../services/taskApi';
import TaskPhotoGrid from './TaskPhotoGrid';
import CleaningChecklistModal from './CleaningChecklistModal';
import ReportIssueModal from './ReportIssueModal';
import { AuthContext } from '../context/AuthContext';
import { TASK_MANAGER_ROLES, FIELD_CREW_ROLES } from '../config/roleConfig';
import { taskTypeBadge } from '../config/taskTypes';
import GuestCheckoutLine from './GuestCheckoutLine';
import GuestAssessmentQuestion, {
  EMPTY_GUEST_ASSESSMENT,
  guestAssessmentProblem,
  guestAssessmentPayload,
  guestAssessmentProblemKey
} from './GuestAssessmentQuestion';
import GuestAssessmentSummary from './GuestAssessmentSummary';
import {
  TASK_PHOTO_MAX_BYTES as MAX_FILE_SIZE,
  TASK_PHOTO_MIME_TYPES as ALLOWED_TYPES,
  TASK_PHOTO_MAX_COUNT as MAX_PHOTOS
} from '../config/taskPhotoRules';

const PHOTO_TYPES = ['before', 'after', 'issue', 'completion'];

const PHOTO_TYPE_COLORS = {
  before: '#2196F3',
  after: '#4CAF50',
  issue: '#FF5722',
  completion: '#471396'
};

const PRIORITY_CONFIG = {
  critical: { bg: 'danger',    emoji: '🔴', labelKey: 'tasks.priority.label.critical' },
  urgent:   { bg: 'warning',   emoji: '🟠', labelKey: 'tasks.priority.label.urgent'   },
  high:     { bg: 'info',      emoji: '🟡', labelKey: 'tasks.priority.label.high'     },
  medium:   { bg: 'success',   emoji: '🟢', labelKey: 'tasks.priority.label.medium'   },
  low:      { bg: 'secondary', emoji: '🔵', labelKey: 'tasks.priority.label.low'      },
};

// The started block takes the cleaning timer's tone (tasks/cleaning-timer.md D8): brand purple
// while on track, the notes-yellow in the last ten minutes, the restock-orange once past the
// target. Orange, not red — "over target" is information, not a failure. The digits stay navy in
// every tone, so the number is always legible and the colour is never the only signal.
const TIMER_TONES = {
  onTrack: { background: '#f3eefc', rule: '#471396' },
  warning: { background: '#fff8e1', rule: '#FFC600' },
  over:    { background: '#fff4e8', rule: '#ff9f53' }
};

const StarRating = ({ rating, onChange }) => {
  const { t } = useLanguage();

  return (
    <div className="mb-2">
      <div className="small text-muted mb-1">{t('tasks.card.completionRating')}</div>
      <div className="d-flex gap-1" dir="ltr" style={{ justifyContent: 'flex-end' }}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className="btn btn-link p-0 border-0"
            style={{ fontSize: '1.5rem', minWidth: '44px', minHeight: '44px', lineHeight: '44px', color: star <= rating ? '#FFC107' : '#dee2e6' }}
            onClick={() => onChange(star === rating ? 0 : star)}
            aria-label={`${star} ${t('tasks.card.stars')}`}
          >
            {star <= rating ? <FaStar /> : <FaRegStar />}
          </button>
        ))}
      </div>
    </div>
  );
};

// A rejected start or undo, in the same affordance the completion error uses further down
// the card — tinted box, leading rule, announced. A bare red line looked like a caption and
// was the one thing on the card a screen reader never mentioned.
const StatusError = ({ message, className = 'mb-2' }) => {
  if (!message) return null;
  return (
    <div
      className={`p-2 rounded ${className}`}
      style={{ background: '#fdecea', borderInlineStart: '3px solid #DC3545' }}
      role="alert"
    >
      <div className="small fw-semibold" style={{ color: '#090040' }}>❌ {message}</div>
    </div>
  );
};

const MaintenanceTaskCard = ({ task, onStart, onRevert, onJoin, onComplete, onTaskChanged, clockOffsetMs = 0 }) => {
  const { t, isArabic, language } = useLanguage();
  const { user } = useContext(AuthContext);
  const [starting, setStarting] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [joining, setJoining] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionRating, setCompletionRating] = useState(0);
  const [completeError, setCompleteError] = useState(null);
  // "Would you host this guest again?" — only on the booking's own turnover clean. The server says
  // when (`guestAssessmentPrompt`); the card never re-derives it.
  const [guestAnswer, setGuestAnswer] = useState(EMPTY_GUEST_ASSESSMENT);

  // Checklist + supplies answers — owned here so the button counters and the
  // sheet never disagree, and so a reopened sheet shows what was just answered.
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [supplies, setSupplies] = useState(task.supplies || []);
  const [openSheet, setOpenSheet] = useState(null);  // 'checklist' | 'supplies' | null
  const [showReportIssue, setShowReportIssue] = useState(false);

  // Photo upload state
  const [photos, setPhotos] = useState(task.photos || []);
  const [selectedType, setSelectedType] = useState('completion');
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const fileInputRef = useRef(null);

  const count = (n) => formatCount(n, language);

  // Same contract `handleComplete` follows: a refusal with a `code` we know is translated into the
  // crew's language (TASK_010 on an undo, TASK_008/009 on a join); otherwise the server's `error`
  // string, and the generic line only when the request never reached it.
  const serverMessage = (err) => {
    const refusalKey = taskRefusalKey(err?.response?.data);
    if (refusalKey) return t(refusalKey);
    return err?.response?.data?.error || t('tasks.card.errorStatus');
  };

  // The API only accepts answers from the assigned cleaner or a supervisor —
  // mirror that here so browsing someone else's task never offers a button
  // that can only fail.
  const isAssignee = !!user?.userId && (task.assignedTo?._id || task.assignedTo) === user.userId;
  // Who took part (tasks/cleaning-performance-report.md D2/D12) — populated as { _id, name } by the
  // API. A partner who joined answers the checklist too, exactly as the server allows.
  const cleaners = task.cleanerUserIds || [];
  const isParticipant = !!user?.userId && cleaners.some(c => (c?._id || c) === user.userId);
  const canAnswer = isAssignee || isParticipant || TASK_MANAGER_ROLES.includes(user?.role);

  const isCleaning = task.type === 'cleaning';
  const isRestock = task.type === 'restock';
  // Anything that is not a clean carries its type on the card, from one place (config/taskTypes.js).
  const typeBadge = taskTypeBadge(task.type);
  const restockItems = task.restockItems || [];
  const hasChecklist = canAnswer && checklist.length > 0;
  const hasSupplies = canAnswer && supplies.length > 0;
  const checklistAnswered = checklist.filter(i => i.status).length;
  const suppliesAnswered = supplies.filter(i => i.status).length;

  // Answering something invalidates a previous "not finished yet" message
  const handleChecklistChange = (next) => { setChecklist(next); setCompleteError(null); };
  const handleSuppliesChange = (next) => { setSupplies(next); setCompleteError(null); };

  // "قائمة الفحص: ١٣ · المستلزمات: ٤" — only the lists that actually have gaps
  const unansweredSummary = (() => {
    const u = completeError?.unanswered;
    if (!u) return null;
    const parts = [];
    if (u.checklist?.length) parts.push(`${t('tasks.card.completeBlockedChecklist')}: ${count(u.checklist.length)}`);
    if (u.supplies?.length) parts.push(`${t('tasks.card.completeBlockedSupplies')}: ${count(u.supplies.length)}`);
    return parts.length ? parts.join(' · ') : null;
  })();

  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const isCompleted = task.status === 'completed';
  const isInProgress = task.status === 'in-progress';
  // Counts only while the clean is under way; the server says whether it is timed at all.
  const timer = useCleanTargetTimer(isInProgress ? task.startedAt : null, task.cleanTargetMinutes, clockOffsetMs);
  const startedTone = TIMER_TONES[timer?.state] || TIMER_TONES.onTrack;

  // Deliberately NOT gated on `canAnswer`. Cleaning tasks are created unassigned and the
  // checkout push fans out to the whole crew, so the person who actually walks into the
  // unit is usually not the assignee. The page is already role-guarded to field staff and
  // the API accepts a status change from any staff member — whoever gets there taps start,
  // and `startedBy` records who it was.
  const canStart = !!onStart && !isCompleted && !isInProgress;
  // «انضممت» — only field crew, only on a clean under way that they are not already on, and only
  // while a place is free. `crewLimit` comes from the server (the same band the join enforces).
  const canJoin = !!onJoin && isCleaning && isInProgress && FIELD_CREW_ROLES.includes(user?.role)
    && !isParticipant && cleaners.length < (task.crewLimit ?? 0);
  const cleanerNames = cleaners.map(c => c?.name).filter(Boolean);
  const hasNotes = !!task.notes;
  const hasBookingRef = !!task.booking?.confirmationCode;
  const hasCompletionFeedback = !!task.completionNotes || !!task.completionRating;
  const hasPhotos = photos.length > 0;
  const canUploadMore = photos.length < MAX_PHOTOS;

  // Both flags MUST be cleared in `finally`, unlike `completing` below. Completion is
  // terminal and moves the card into a different <section>, forcing a remount with fresh
  // state; start and undo keep the task in the same section under the same `key`, so React
  // reuses THIS instance and every flag survives. Leave one set and the round trip
  // start → undo comes back with the start button disabled behind a dead spinner.
  const handleStart = async () => {
    if (!canStart || starting) return;
    setStarting(true);
    setStatusError(null);
    try {
      await onStart(task._id);
    } catch (err) {
      setStatusError(serverMessage(err));
    } finally {
      setStarting(false);
    }
  };

  // Undo a mis-tap. No confirmation dialog: the action is itself reversible — the start
  // button comes straight back — so a modal would cost more taps than the mistake.
  const handleRevert = async () => {
    if (!onRevert || reverting) return;
    setReverting(true);
    setStatusError(null);
    try {
      await onRevert(task._id, task.assignedTo ? 'assigned' : 'pending');
    } catch (err) {
      setStatusError(serverMessage(err));
    } finally {
      setReverting(false);
    }
  };

  // Same flag rule as start and undo: the card stays in its section, so clear in `finally`.
  const handleJoin = async () => {
    if (!canJoin || joining) return;
    setJoining(true);
    setStatusError(null);
    try {
      await onJoin(task._id);
    } catch (err) {
      setStatusError(serverMessage(err));
    } finally {
      setJoining(false);
    }
  };

  const handleComplete = async () => {
    if (isCompleted || completing) return;

    // Checked here first so the crew reads what is missing without a round trip; the server applies
    // the same rule (guestAssessmentService) whatever this screen does.
    const guestProblem = guestAssessmentProblem(guestAnswer, task.guestAssessmentPrompt, photos);
    if (guestProblem) {
      setCompleteError({ message: t(guestAssessmentProblemKey(guestProblem)), unanswered: null });
      return;
    }

    setCompleting(true);
    setCompleteError(null);
    try {
      await onComplete(task._id, {
        completionNotes: completionNotes.trim() || null,
        completionRating: completionRating || null,
        guestAssessment: guestAssessmentPayload(guestAnswer)
      });
    } catch (err) {
      // A rejected close must never look like "nothing happened" — the crew
      // needs to read why, right above the button they just tapped.
      //
      // Contract: the server's `code` is the stable identifier and we translate
      // it ourselves; its `error` string is Arabic-only and exists for legacy
      // clients and logs. Endpoints that don't send a code yet (most of them)
      // fall back to displaying that string — never infer meaning from it.
      const data = err?.response?.data;
      // `details` is the canonical field; `data` is the deprecated duplicate
      // the first release shipped with — read both while it rolls out.
      const unanswered = data?.details?.unanswered || data?.data?.unanswered || null;
      const refusalKey = taskRefusalKey(data);
      setCompleteError({
        message: refusalKey ? t(refusalKey) : (data?.error || t('tasks.card.completeFailed')),
        unanswered
      });
      setCompleting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset input
    if (!file) return;

    setPhotoError(null);

    // Client-side validation
    if (!ALLOWED_TYPES.includes(file.type)) {
      setPhotoError(t('tasks.photos.invalidType'));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setPhotoError(t('tasks.photos.tooLarge'));
      return;
    }
    if (photos.length >= MAX_PHOTOS) {
      setPhotoError(t('tasks.photos.maxReached'));
      return;
    }

    setUploading(true);
    try {
      const result = await taskApi.uploadTaskPhoto(task._id, file, selectedType);
      setPhotos(prev => [...prev, result.data.photo]);
    } catch {
      setPhotoError(t('tasks.photos.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (deletingPhotoId) return; // Guard against double-tap
    setDeletingPhotoId(photoId);
    try {
      await taskApi.deleteTaskPhoto(task._id, photoId);
      setPhotos(prev => prev.filter(p => p._id !== photoId));
    } catch (err) {
      // e.g. the last photo behind a recorded «لا» — say so, not "upload failed"
      const refusalKey = taskRefusalKey(err?.response?.data);
      setPhotoError(refusalKey ? t(refusalKey) : t('tasks.photos.uploadFailed'));
    } finally {
      setDeletingPhotoId(null);
    }
  };

  return (
    <Card
      dir={isArabic ? 'rtl' : 'ltr'}
      className="mb-3 border-0 shadow-sm"
      style={{
        borderRight: `4px solid var(--bs-${priority.bg})`,
        opacity: isCompleted ? 0.6 : 1,
      }}
    >
      <Card.Body className="p-3">

        {/* Top row: priority badge + overdue + completed */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <Badge bg={priority.bg} className="px-2 py-1">
              {priority.emoji} {t(priority.labelKey)}
            </Badge>
            {task.isOverdue && (
              <Badge bg="danger">{t('tasks.card.overdue')}</Badge>
            )}
            {typeBadge && (
              <Badge style={{ backgroundColor: typeBadge.color }}>
                {typeBadge.icon} {t(`tasks.type.${task.type}`)}
              </Badge>
            )}
          </div>
          {isCompleted && (
            <Badge bg="success">✓ {t('tasks.status.completed')}</Badge>
          )}
        </div>

        {/* Task title + ticket ID */}
        <div className="fw-bold fs-6 mb-1">{task.title}</div>
        {task.ticketId && (
          <div className="small text-primary fw-semibold mb-2">{t('tasks.card.ticketRef')}: {task.ticketId}</div>
        )}

        {/* Location — property name (PMS) or service location name (service) */}
        {task.displayLocationName && (
          <div className="mb-1">
            <span className="text-primary fw-semibold">
              📍 {task.displayLocationName}
              {task.displayLocationCode && (
                <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.75em' }} dir="ltr">{task.displayLocationCode}</span>
              )}
            </span>
            {task.property?.address && (
              <div className="small text-muted">{task.property.address}</div>
            )}
          </div>
        )}

        {/* Scheduled time */}
        <div className="small text-muted mb-2">
          🕐 {formatDate(task.scheduledDate, {}, language)}
          {task.scheduledTime && ` • ${task.scheduledTime}`}
        </div>

        {/* Guest checkout — ALWAYS VISIBLE, deliberately.
            tasks/manual-guest-checkout-event.md D43.

            It sits here, beside the scheduled time, and NOT inside the «show details» collapse
            below where the other booking fields live. The push notification that told this person a
            guest had left points at this card; a fact hidden behind a tap would defeat the message
            that brought them here. It is also the answer to the question the schedule alone cannot
            settle — the task says 11:00, and this says whether the room is actually free. */}
        {task.guestCheckout && (
          <GuestCheckoutLine checkout={task.guestCheckout} compact className="small mb-2" />
        )}

        {/* Start — deliberately ABOVE the notes, checklist and photo block, not beside
            «مكتمل» at the foot of the card. This is the tap made on the doorstep with one
            thumb: it must be reachable without scrolling past a checklist nobody has
            opened yet. Solid brand purple so it reads as the single next action while the
            green «مكتمل» stays the only finishing move; once tapped it is replaced in
            place by the started line, so the card never offers both at once. */}
        {canStart && (
          <div className="mb-3">
            <StatusError message={statusError} />
            <Button
              className="w-100 fw-bold"
              style={{
                minHeight: '48px',
                fontSize: '1.05rem',
                background: '#471396',
                borderColor: '#471396'
              }}
              onClick={handleStart}
              disabled={starting}
            >
              {starting
                ? <Spinner animation="border" size="sm" />
                : `▶ ${isCleaning ? t('tasks.card.startButtonCleaning') : t('tasks.card.startButton')}`
              }
            </Button>
          </div>
        )}

        {/* Started — the same slot the button occupied, so the card answers "did anyone
            pick this up?" in the place the crew last looked. */}
        {isInProgress && (
          <div
            className="mb-3 p-2 rounded"
            style={{ background: startedTone.background, borderInlineStart: `3px solid ${startedTone.rule}` }}
          >
            {/* Two rows, not one. The label and the «بدأت …» meta were siblings on a single
                line separated by a margin, which bidi reorders when the interface language is
                English inside this RTL card — the two strings ran together, and a long name
                wrapped leaving one word orphaned under the undo link. Stacking them removes
                the dependency on inline direction entirely. */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span className="fw-semibold" style={{ color: '#471396' }}>
                ⏱ {t('tasks.status.inProgress')}
              </span>
              {/* Undo — quiet on purpose. It must be findable by someone who knows they
                  mis-tapped, without competing with the work itself. Full 44px target
                  even though it looks like a link: a fat-finger miss here is what put
                  them in this state in the first place. */}
              {onRevert && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-secondary flex-shrink-0"
                  style={{ fontSize: '0.85rem', minHeight: '44px', minWidth: '44px', textDecoration: 'underline' }}
                  onClick={handleRevert}
                  disabled={reverting}
                >
                  {reverting
                    ? <Spinner animation="border" size="sm" />
                    : t('tasks.card.undoStart')
                  }
                </button>
              )}
            </div>
            {/* The cleaning target timer — the one large thing on the card while a clean runs.
                The clock is its own LTR run so ":" and the leading "+" hold their places in RTL. */}
            {timer && (
              <div className="d-flex align-items-baseline flex-wrap column-gap-2 my-1" role="timer">
                <span
                  dir="ltr"
                  style={{
                    fontSize: '2rem', fontWeight: 700, lineHeight: 1.15,
                    color: '#090040', fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {timer.state === 'over' && '+'}{formatClockDuration(timer.seconds, language)}
                </span>
                <span className="small fw-semibold" style={{ color: '#090040' }}>
                  {timer.state === 'over'
                    ? `${t('tasks.card.timerOver')} (${count(timer.targetMinutes)} ${t('tasks.card.minutes')})`
                    : `${t('tasks.card.timerLeftOf')} ${count(timer.targetMinutes)} ${t('tasks.card.minutes')}`}
                </span>
              </div>
            )}
            {task.startedAt && (
              <div className="small text-muted">
                {t('tasks.card.startedAt')} {formatRelativeTime(task.startedAt, language)}
                {task.startedBy && ` • ${task.startedBy}`}
              </div>
            )}
            {/* Who is on this clean — the people credited with it, not who it was assigned to. */}
            {cleanerNames.length > 0 && (
              <div className="small text-muted">
                👥 {t('tasks.card.cleanersLabel')}: {cleanerNames.join(' · ')}
              </div>
            )}
            {/* «انضممت» — the partner's own tap. Secondary to the work in progress: outlined, full
                width, the same 48px target as start, because it is tapped on a phone in a unit. */}
            {canJoin && (
              <Button
                variant="outline-primary"
                className="w-100 fw-semibold mt-2"
                style={{ minHeight: '48px', color: '#471396', borderColor: '#471396' }}
                onClick={handleJoin}
                disabled={joining}
              >
                {joining ? <Spinner animation="border" size="sm" /> : `👥 ${t('tasks.card.joinButton')}`}
              </Button>
            )}
            <StatusError message={statusError} className="mt-2 mb-0" />
          </div>
        )}

        {/* Internal notes — always visible, ops instructions for field staff */}
        {hasNotes && (
          <div className="mb-3 p-2 rounded" style={{ background: '#fff8e1', borderRight: '3px solid #FFC600' }}>
            <div className="small text-muted mb-1">📋 {t('tasks.card.internalNotes')}</div>
            <div className="small fw-semibold">{task.notes}</div>
          </div>
        )}

        {/* Details toggle — booking ref + completion timestamp */}
        {(hasBookingRef || (isCompleted && task.completedAt)) && (
          <>
            <button
              className="btn btn-link btn-sm p-0 mb-2 text-secondary d-flex align-items-center gap-1"
              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
              onClick={() => setShowDetails(!showDetails)}
              type="button"
            >
              {showDetails ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              {showDetails ? t('tasks.card.hideDetails') : t('tasks.card.showDetails')}
            </button>

            <Collapse in={showDetails}>
              <div className="mb-2">
                {hasBookingRef && (
                  <div className="small mb-1">
                    <span className="text-muted">{t('tasks.card.bookingRef')}: </span>
                    <span className="text-info fw-semibold">{task.booking.confirmationCode}</span>
                  </div>
                )}
                {isCompleted && task.completedAt && (
                  <div className="small text-success">
                    ✓ {t('tasks.card.completedAt')} {formatDate(task.completedAt, {}, language)}
                    {task.completedBy && ` • ${task.completedBy}`}
                  </div>
                )}
              </div>
            </Collapse>
          </>
        )}

        {/* Completed task: show field team feedback + photos (read-only) */}
        {isCompleted && (hasCompletionFeedback || hasPhotos || task.guestAssessment) && (
          <div className="mb-3 p-2 rounded" style={{ background: '#e8f5e9', borderRight: '3px solid #4CAF50' }}>
            <div className="small text-muted mb-1">{t('tasks.card.fieldTeamReport')}</div>
            <GuestAssessmentSummary assessment={task.guestAssessment} />
            {task.completionRating && (
              <div className="mb-1" dir="ltr" style={{ textAlign: 'right' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <FaStar key={star} style={{ color: star <= task.completionRating ? '#FFC107' : '#dee2e6', fontSize: '1rem' }} />
                ))}
              </div>
            )}
            {task.completionNotes && (
              <div className="small">{task.completionNotes}</div>
            )}
            <TaskPhotoGrid photos={photos} />
          </div>
        )}

        {/* Completion feedback inputs — only for active tasks */}
        {!isCompleted && (
          <div className="mb-3">

            {/* Restock task — show the items to refill, not a checklist */}
            {isRestock && (
              <div className="mb-3 p-2 rounded" style={{ background: '#fff4e8', borderInlineStart: '3px solid #ff9f53' }}>
                <div className="small fw-semibold mb-1" style={{ color: '#090040' }}>
                  🧴 {t('tasks.checklist.restockTitle')}
                </div>
                {restockItems.length > 0 ? (
                  <ul className="mb-0 ps-3">
                    {restockItems.map(item => (
                      <li key={item.itemId} style={{ fontSize: '0.9rem' }}>
                        {isArabic ? item.labelAr : (item.labelEn || item.labelAr)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="small text-muted">{t('tasks.checklist.restockEmpty')}</div>
                )}
              </div>
            )}

            {/* Checklist + supplies — cleaning tasks that carry a template only */}
            {(hasChecklist || hasSupplies) && (
              <div className="d-flex gap-2 mb-3">
                {hasChecklist && (
                  <Button
                    variant="outline-primary"
                    className="flex-fill fw-semibold"
                    style={{ minHeight: '48px', borderColor: '#471396', color: '#471396' }}
                    onClick={() => setOpenSheet('checklist')}
                  >
                    📋 {t('tasks.checklist.openButton')}
                    {/* No forced LTR — the counter reads with the page direction */}
                    <span style={{ fontSize: '0.85rem', marginInlineStart: '6px' }}>
                      {formatProgress(checklistAnswered, checklist.length, language)}
                    </span>
                  </Button>
                )}
                {hasSupplies && (
                  <Button
                    variant="outline-primary"
                    className="flex-fill fw-semibold"
                    style={{ minHeight: '48px', borderColor: '#471396', color: '#471396' }}
                    onClick={() => setOpenSheet('supplies')}
                  >
                    🧴 {t('tasks.checklist.suppliesButton')}
                    <span style={{ fontSize: '0.85rem', marginInlineStart: '6px' }}>
                      {formatProgress(suppliesAnswered, supplies.length, language)}
                    </span>
                  </Button>
                )}
              </div>
            )}

            {/* Report an issue — any active cleaning task the crew can act on */}
            {isCleaning && canAnswer && (
              <Button
                variant="outline-danger"
                className="w-100 fw-semibold mb-3"
                style={{ minHeight: '48px' }}
                onClick={() => setShowReportIssue(true)}
              >
                🛠️ {t('tasks.reportIssue.button')}
              </Button>
            )}

            <StarRating rating={completionRating} onChange={setCompletionRating} />

            <Form.Control
              as="textarea"
              rows={2}
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder={t('tasks.card.completionNotesPlaceholder')}
              maxLength={1000}
              className="mb-3"
              dir={isArabic ? 'rtl' : 'ltr'}
              style={{ fontSize: '0.9rem', textAlign: isArabic ? 'right' : 'left' }}
            />

            {/* ── Photo Upload Section ── */}
            <div
              className="mb-3 p-2 rounded"
              style={{
                background: '#f8f6fc',
                border: '1px dashed #c4b5d9'
              }}
            >
              {/* Photo type selector — pill badges */}
              <div className="small text-muted mb-2">{t('tasks.photos.selectType')}</div>
              <div className="d-flex flex-wrap gap-1 mb-2">
                {PHOTO_TYPES.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedType(type)}
                    style={{
                      minHeight: '36px',
                      padding: '4px 14px',
                      border: `2px solid ${PHOTO_TYPE_COLORS[type]}`,
                      borderRadius: '20px',
                      background: selectedType === type ? PHOTO_TYPE_COLORS[type] : 'transparent',
                      color: selectedType === type ? '#fff' : PHOTO_TYPE_COLORS[type],
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {t(`tasks.photos.${type}`)}
                  </button>
                ))}
              </div>

              {/* Upload button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={uploading}
              />

              <Button
                variant="outline-primary"
                className="w-100 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                style={{
                  minHeight: '48px',
                  fontSize: '0.95rem',
                  borderColor: '#471396',
                  color: uploading ? '#999' : '#471396',
                  borderStyle: 'solid'
                }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !canUploadMore}
              >
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    {t('tasks.photos.uploading')}
                  </>
                ) : !canUploadMore ? (
                  t('tasks.photos.maxReached')
                ) : (
                  <>
                    <FaCamera size={18} />
                    {t('tasks.photos.upload')}
                  </>
                )}
              </Button>

              {/* Error message */}
              {photoError && (
                <div className="small text-danger mt-1">{photoError}</div>
              )}

              {/* Uploaded photos preview grid */}
              {hasPhotos && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {photos.map((photo, idx) => (
                    <div
                      key={photo._id || idx}
                      style={{
                        position: 'relative',
                        width: '72px',
                        height: '54px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `2px solid ${PHOTO_TYPE_COLORS[photo.type] || '#dee2e6'}`
                      }}
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || `${photo.type} ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Type label */}
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          textAlign: 'center',
                          color: '#fff',
                          background: PHOTO_TYPE_COLORS[photo.type] || '#666',
                          padding: '1px 0',
                          lineHeight: 1.2
                        }}
                      >
                        {t(`tasks.photos.${photo.type}`)}
                      </span>
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(photo._id)}
                        disabled={!!deletingPhotoId}
                        style={{
                          position: 'absolute',
                          top: '-1px',
                          right: '-1px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          border: 'none',
                          background: deletingPhotoId === photo._id ? 'rgba(150, 150, 150, 0.9)' : 'rgba(220, 53, 69, 0.9)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                          lineHeight: 1
                        }}
                        aria-label={t('tasks.photos.deleteConfirm')}
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Photo count */}
              {hasPhotos && (
                <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                  {photos.length}/{MAX_PHOTOS} {t('tasks.photos.photoCount')}
                </div>
              )}
            </div>
            {/* ── End Photo Upload Section ── */}

            {/* The guest question — the last step before «مكتمل», because it is part of closing the
                clean (L5). Its «لا» photo button uploads an `issue` photo into the same list the
                section above shows. Renders nothing when the server does not ask. */}
            <GuestAssessmentQuestion
              taskId={task._id}
              prompt={task.guestAssessmentPrompt}
              value={guestAnswer}
              onChange={(next) => { setGuestAnswer(next); setCompleteError(null); }}
              photos={photos}
              onPhotoAdded={(photo) => setPhotos(prev => [...prev, photo])}
              disabled={completing}
            />

            {/* Blocked/failed completion — sits directly above the button that
                was tapped, so the reason is impossible to miss on a phone */}
            {completeError && (
              <div
                className="mb-2 p-2 rounded"
                style={{
                  background: completeError.unanswered ? '#fff4e8' : '#fdecea',
                  // Logical property — leading edge in both RTL and LTR
                  borderInlineStart: `3px solid ${completeError.unanswered ? '#ff9f53' : '#DC3545'}`
                }}
                role="alert"
              >
                <div className="small fw-semibold" style={{ color: '#090040' }}>
                  {completeError.unanswered ? '⚠️' : '❌'} {completeError.message}
                </div>
                {unansweredSummary && (
                  <div className="small text-muted mt-1">
                    {t('tasks.card.completeBlockedRemainingLabel')} — {unansweredSummary}
                  </div>
                )}
              </div>
            )}

            {/* Complete button — primary action, full width, large touch target */}
            <Button
              variant="success"
              className="w-100 fw-bold"
              style={{ minHeight: '48px', fontSize: '1rem' }}
              onClick={handleComplete}
              disabled={completing}
            >
              {completing
                ? <Spinner animation="border" size="sm" />
                : `✓ ${t('tasks.card.completeButton')}`
              }
            </Button>
          </div>
        )}

      </Card.Body>

      <CleaningChecklistModal
        show={openSheet === 'checklist'}
        onHide={() => setOpenSheet(null)}
        taskId={task._id}
        mode="checklist"
        items={checklist}
        onItemsChange={handleChecklistChange}
      />

      <CleaningChecklistModal
        show={openSheet === 'supplies'}
        onHide={() => setOpenSheet(null)}
        taskId={task._id}
        mode="supplies"
        items={supplies}
        onItemsChange={handleSuppliesChange}
      />

      <ReportIssueModal
        show={showReportIssue}
        onHide={() => setShowReportIssue(false)}
        taskId={task._id}
        onReported={onTaskChanged}
      />
    </Card>
  );
};

export default MaintenanceTaskCard;
